import { ProjectService } from '@/api/project';
import { ITestPlanSchema } from '@/api/testPlan';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { CreateButton } from '@/components/shared/button/CreateButton';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import { DeleteTestPlan } from '@/components/testPlan/DeleteTestPlan';
import { ExportTestPlan } from '@/components/testPlan/ExportTestPlan';
import { ImportTestPlan } from '@/components/testPlan/ImportTestPlan';
import { CustomCell } from '@/components/testRun/CustomCell';
import { useAccess } from '@/utils/hooks/useAccess';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import React, { useRef } from 'react';
import { BiSolidShow } from 'react-icons/bi';
import { FaArchive } from 'react-icons/fa';
import { FaPen } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';

interface CustomActionCellProps {
  data: ITestPlanSchema;
}

export const ListTestPlan: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  const isViewer = useAccess(id!);

  const filterSpec: IFilterSpec[] = [
    { name: 'plan_name', label: 'Plan Name', type: 'text' },
    { name: 'created_at', label: 'Created Date', type: 'date' },
  ];

  const spec = useCustomSpec('/api/crud/test-plan', {
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'plan_name', label: 'Plan Name' },
      { key: 'description', label: 'Description' },
      { key: 'created_at', label: 'Created Date' },
    ],
  });

  const { data: projectData, isFetching: isProjectFetching } =
    ProjectService.useGet(id);

  const toArchive = () => navigate('archive');

  const CustomActionCell: React.FC<CustomActionCellProps> = ({ data }) => {
    return !isViewer ? (
      <>
        <LinkButton path={data.id} icon={<BiSolidShow size={20} />} />

        <LinkButton path={`${data.id}/edit`} icon={<FaPen size={15} />} />

        <DeleteTestPlan
          id={data.id!}
          onSuccess={() => ref.current?.refetch()}
        />
      </>
    ) : (
      <LinkButton path={data.id} icon={<BiSolidShow size={20} />} />
    );
  };

  if (isProjectFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <Section
      title={`Test Plan - ${projectData?.title}`}
      actions={
        !isViewer ? (
          <div className="flex flex-row gap-3">
            <ExportTestPlan id={id} projectName={projectData?.title} />
            <ImportTestPlan id={id}></ImportTestPlan>
            <OutlineButton icon={<FaArchive />} onClick={toArchive}>
              Archive
            </OutlineButton>
            <CreateButton label="Add Test Plan" />
          </div>
        ) : (
          <></>
        )
      }
    >
      <ExtendedTableLowCode
        ref={ref}
        defaultFilter={{
          project_id: id,
          is_archive: 'false',
        }}
        defaultSort="plan_name"
        defaultSortOrder="desc"
        spec={spec}
        customButtonCreate={HideCustomCreateButton}
        customActionCell={data => (
          <CustomActionCell data={data as ITestPlanSchema} />
        )}
        customCell={CustomCell}
        filterSpec={filterSpec}
        showFilter
      />
    </Section>
  );
};
